import { useEffect, useMemo, useState } from 'react';

import { setAuthToken } from '../../../../api/authToken';
import { setSessionExpiredHandler } from '../../../../api/sessionExpiry';
import { AuthContext } from '../../core/AuthContext';
import { AUTH_MODES } from '../../core/authModes';
import { simulatedUsers } from './simulatedUsers';

/* mirrors the standard OIDC claims a Keycloak token would carry, so
   the user page shows meaningful values in simulated dev mode too */
const createSimulatedToken = (simulatedUser) => {
  const issuedAt = Math.floor(Date.now() / 1000);
  const [givenName, familyName] = simulatedUser.name.split(' ');

  return window.btoa(
    JSON.stringify({
      exp: issuedAt + 8 * 60 * 60,
      iat: issuedAt,
      iss: 'simulated-auth',
      aud: 'hsadmin-ng-ui',
      sub: simulatedUser.uuid,
      preferred_username: simulatedUser.login,
      given_name: givenName,
      family_name: familyName,
      email: simulatedUser.email,
      simulated: true,
    })
  );
};

/* there is no renewal for the simulated token — an expired session
   only leads back to the login page */
const isSessionExpired = (session) => {
  try {
    const claims = JSON.parse(window.atob(session.token));
    return typeof claims.exp === 'number' && claims.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

export const SimulatedAuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => {
    const storedSession = window.localStorage.getItem('simulatedAuthSession');
    if (!storedSession) return null;

    const parsedSession = JSON.parse(storedSession);
    if (isSessionExpired(parsedSession)) {
      window.localStorage.removeItem('simulatedAuthSession');
      return null;
    }
    return parsedSession;
  });

  const token = session?.token;

  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  /*
   * A 401 from the API says nothing about a simulated session: no
   * backend issued this token, so none of them can pronounce it
   * expired. A backend that rejects it — every backend that checks
   * signatures does — used to end the session here, which turned "the
   * backend does not accept this token" into "you have been logged
   * out" and bounced the reader straight back to the login page. Only
   * the token's own `exp` decides; a 401 on a session that is still
   * valid by that measure belongs to the request that provoked it and
   * surfaces there.
   */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setSession((currentSession) => {
        if (!currentSession || !isSessionExpired(currentSession)) {
          return currentSession;
        }
        window.localStorage.removeItem('simulatedAuthSession');
        return null;
      });
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const value = useMemo(
    () => ({
      authMode: AUTH_MODES.SIMULATED,
      user: session?.user,
      token: session?.token,
      isAuthenticated: Boolean(session),
      isLoading: false,
      login: ({ username, password } = {}) => {
        const simulatedUser = simulatedUsers.find(
          (user) => user.username === username && user.password === password
        );

        if (!simulatedUser) {
          throw new Error('Wrong username or password.');
        }

        const user = {
          sub: simulatedUser.uuid,
          name: simulatedUser.name,
          email: simulatedUser.email,
        };

        const nextSession = {
          user,
          token: createSimulatedToken(simulatedUser),
        };

        window.localStorage.setItem(
          'simulatedAuthSession',
          JSON.stringify(nextSession)
        );

        setSession(nextSession);

        return nextSession;
      },
      logout: () => {
        window.localStorage.removeItem('simulatedAuthSession');
        setSession(null);
      },
      error: null,
    }),
    [session]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
